import "@/styles/globals.css";
import "@/lib/icons";
import "flag-icons/css/flag-icons.min.css";
import type { AppProps } from "next/app";
import type { ReactElement, ReactNode } from "react";
import type { NextPage } from "next";
import { useEffect } from "react";
import { useRouter } from "next/router";
import { PostHogProvider } from "@posthog/react";

import { UserProvider } from "@/context/UserContext";
import { FeatureFlagProvider } from "@/context/FeatureFlagContext";
import { ToastProvider } from "@/context/ToastContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ToastContainer from "@/components/ui/ToastContainer";
import ErrorBoundary from "@/components/ErrorBoundary";
import posthog, { initPostHog } from "@/lib/posthog";

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

initPostHog();

// before_send (lib/posthog.ts) drops discrete events on /admin, but session
// replay is a separate recording pipeline it doesn't touch -- this stops
// recording outright while on /admin and resumes on the way back out, so an
// admin's screen is never captured. Route-based, not person-based: this
// still won't run until Next has hydrated and the router is ready, so the
// icing recommendation is the belt to this suspenders -- also add /admin as
// a URL exclusion in PostHog → Project Settings → Session Replay, which
// takes effect before any client code runs at all.
function AdminRecordingGuard() {
  const router = useRouter();
  useEffect(() => {
    const isAdmin = router.pathname.startsWith("/admin");
    if (isAdmin) posthog.stopSessionRecording();
    else posthog.startSessionRecording();
  }, [router.pathname]);
  return null;
}

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  const getLayout = Component.getLayout ?? (page => page);

  return (
    <ErrorBoundary>
      <PostHogProvider client={posthog}>
        <AdminRecordingGuard />
        <UserProvider>
          <FeatureFlagProvider>
            <ThemeProvider>
              <ToastProvider>
                {getLayout(<Component {...pageProps} />)}
                <ToastContainer />
              </ToastProvider>
            </ThemeProvider>
          </FeatureFlagProvider>
        </UserProvider>
      </PostHogProvider>
    </ErrorBoundary>
  );
}
